import React from 'react'
import { Button, Heading, Text, LoadingSpinner, Input } from '../../components/ui'
import { admDespesasService } from '../../services/admDespesas'
import { logDelete } from '../../services/userLogs'
import { printService } from '../../services/printService'
import { downloadXlsx } from '../../utils/downloadXlsx'
import type { UnifiedDocument } from '../../types/admDespesas'
import type { PrintConfig, PrintColumn, PrintRow } from '../../types/print'
import { ArrowDownTrayIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'

const PAGE_SIZE = 20

type PrintScope = 'page' | 'filtered'

export type AdmDespesasDocumentSource = UnifiedDocument['source']

type AllDocumentsFilters = {
  source: '' | AdmDespesasDocumentSource
  issuer: string
  dateFrom: string
  dateTo: string
}

const EMPTY_FILTERS: AllDocumentsFilters = {
  source: '',
  issuer: '',
  dateFrom: '',
  dateTo: '',
}

const SOURCE_FILTER_OPTIONS: Array<{ value: '' | AdmDespesasDocumentSource; label: string }> = [
  { value: '', label: 'Tipo: Todos' },
  { value: 'nfe', label: 'NF-e' },
  { value: 'nfse', label: 'NFS-e' },
  { value: 'sem_nf', label: 'Planilha (sem NF)' },
  { value: 'contracts', label: 'Contrato' },
  { value: 'budgets', label: 'Orçamentos/Pedidos' },
]

function summarizePrintFilters(entries: Array<[string, string]>): Array<{ label: string; value: string }> {
  return entries
    .map(([label, value]) => [label, value.trim()] as const)
    .filter(([, value]) => value.length > 0)
    .map(([label, value]) => ({ label, value }))
}

function hasActiveFilters(filters: AllDocumentsFilters): boolean {
  return !!(filters.source || filters.issuer.trim() || filters.dateFrom || filters.dateTo)
}

function documentMatchesFilters(doc: UnifiedDocument, filters: AllDocumentsFilters): boolean {
  if (filters.source && doc.source !== filters.source) return false

  const issuerQuery = filters.issuer.trim().toLowerCase()
  if (issuerQuery) {
    const haystack = [doc.issuerName, doc.fileName].filter(Boolean).join(' ').toLowerCase()
    if (!haystack.includes(issuerQuery)) return false
  }

  if (filters.dateFrom || filters.dateTo) {
    if (!doc.date) return false
    const docDate = doc.date.slice(0, 10)
    if (filters.dateFrom && docDate < filters.dateFrom) return false
    if (filters.dateTo && docDate > filters.dateTo) return false
  }

  return true
}

function activeFilterSummary(filters: AllDocumentsFilters): Array<{ label: string; value: string }> {
  const sourceLabel = SOURCE_FILTER_OPTIONS.find((o) => o.value === filters.source)?.label ?? ''
  return summarizePrintFilters([
    ['Tipo', filters.source ? sourceLabel.replace(/^Tipo: /, '') : ''],
    ['Emissor/Descrição', filters.issuer],
    ['Data de', filters.dateFrom],
    ['Data até', filters.dateTo],
  ])
}

export type AdmDespesasAllDocumentsTabProps = {
  onViewDocument: (doc: UnifiedDocument) => void
  onEditDocument: (doc: UnifiedDocument) => void
  refreshKey?: number
  canEdit: boolean
  canDelete: boolean
  userId: string | undefined
  onError: (message: string | null) => void
  getErrorMessage: (error: unknown) => string
}

function formatCurrency(value: number | null): string {
  if (value == null) return '—'
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('pt-BR')
}

function sourceBadgeClass(source: AdmDespesasDocumentSource): string {
  switch (source) {
    case 'nfe':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
    case 'nfse':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200'
    case 'sem_nf':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
    case 'contracts':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
    case 'budgets':
      return 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200'
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  }
}

function documentLabel(doc: UnifiedDocument): string {
  const name = doc.issuerName?.trim() || doc.fileName?.trim() || doc.id.slice(0, 8)
  return `${doc.sourceLabel}: ${name}`
}

const AdmDespesasAllDocumentsTab: React.FC<AdmDespesasAllDocumentsTabProps> = ({
  onViewDocument,
  onEditDocument,
  refreshKey = 0,
  canEdit,
  canDelete,
  userId,
  onError,
  getErrorMessage,
}) => {
  const [documents, setDocuments] = React.useState<UnifiedDocument[]>([])
  const [loading, setLoading] = React.useState(true)
  const [filters, setFilters] = React.useState<AllDocumentsFilters>(EMPTY_FILTERS)
  const [page, setPage] = React.useState(1)
  const [deleteModal, setDeleteModal] = React.useState<UnifiedDocument | null>(null)
  const [deleteReason, setDeleteReason] = React.useState('')
  const [submittingDelete, setSubmittingDelete] = React.useState(false)
  const [printDialogOpen, setPrintDialogOpen] = React.useState(false)
  const [exportingExcel, setExportingExcel] = React.useState(false)

  const loadDocuments = React.useCallback(async () => {
    setLoading(true)
    onError(null)
    try {
      const rows = await admDespesasService.listAllDocuments()
      setDocuments(rows)
    } catch (error) {
      onError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [getErrorMessage, onError])

  React.useEffect(() => {
    void loadDocuments()
  }, [loadDocuments, refreshKey])

  React.useEffect(() => {
    setPage(1)
  }, [filters])

  const filtered = React.useMemo(
    () => documents.filter((doc) => documentMatchesFilters(doc, filters)),
    [documents, filters],
  )

  const filtersActive = hasActiveFilters(filters)

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS)
    setPage(1)
  }

  const pagesCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageStart = (page - 1) * PAGE_SIZE
  const paginated = filtered.slice(pageStart, pageStart + PAGE_SIZE)

  const closeDeleteModal = () => {
    setDeleteModal(null)
    setDeleteReason('')
  }

  const confirmDelete = async () => {
    if (!deleteModal || !userId || !deleteReason.trim()) return

    const reason = deleteReason.trim()
    const label = documentLabel(deleteModal)
    setSubmittingDelete(true)
    try {
      if (deleteModal.source === 'nfe') {
        await admDespesasService.softDeleteNfe(deleteModal.id, reason, userId)
      } else if (deleteModal.source === 'nfse') {
        await admDespesasService.softDeleteNfse(deleteModal.id, reason, userId)
      } else if (deleteModal.source === 'sem_nf') {
        await admDespesasService.softDeleteWithoutNf(deleteModal.id, reason, userId)
      } else if (deleteModal.source === 'contracts') {
        await admDespesasService.softDeleteContract(deleteModal.id, reason, userId)
      } else {
        await admDespesasService.softDeleteOrderHeader(deleteModal.id, reason, userId)
      }

      setDocuments((prev) => prev.filter((doc) => doc.id !== deleteModal.id))
      await logDelete('other', deleteModal.id, `Excluiu documento unificado: ${label}`, {
        additional_info: `Motivo: ${reason}`,
      })
      closeDeleteModal()
    } catch (error) {
      onError(getErrorMessage(error))
    } finally {
      setSubmittingDelete(false)
    }
  }

  const buildPrintPayload = (scope: PrintScope): PrintConfig => {
    const generatedAt = new Date().toLocaleString('pt-BR')
    const scopeLabel = scope === 'page' ? 'Página atual' : 'Todos os filtrados'
    const source = scope === 'page' ? paginated : filtered

    const columns: PrintColumn[] = [
      { key: 'source_label', label: 'Tipo' },
      { key: 'issuer_name', label: 'Emissor/Descrição' },
      { key: 'date', label: 'Data' },
      { key: 'total_value', label: 'Valor', align: 'right' },
      { key: 'created_at', label: 'Criado em' },
    ]

    const rows: PrintRow[] = source.map((doc) => ({
      source_label: doc.sourceLabel,
      issuer_name: doc.issuerName?.trim() || '—',
      date: formatDate(doc.date),
      total_value: formatCurrency(doc.totalValue),
      created_at: formatDate(doc.createdAt),
    }))

    return {
      reportTitle: 'ADM • Despesas • Todos os Documentos',
      generatedAt,
      scopeLabel,
      totalRows: rows.length,
      filters: activeFilterSummary(filters),
      columns,
      rows,
    }
  }

  const handlePrint = (scope: PrintScope) => {
    printService.print(buildPrintPayload(scope))
    setPrintDialogOpen(false)
  }

  const exportFilteredExcel = () => {
    if (filtered.length === 0 || exportingExcel) return

    const rows = filtered.map((doc) => ({
      Tipo: doc.sourceLabel,
      'Emissor/Descrição': doc.issuerName?.trim() || '—',
      Data: formatDate(doc.date),
      Valor: doc.totalValue ?? '',
      'Criado em': formatDate(doc.createdAt),
    }))

    setExportingExcel(true)
    onError(null)
    try {
      const dateStamp = new Date().toISOString().slice(0, 10)
      downloadXlsx('Documentos', `adm-despesas-todos-documentos-${dateStamp}.xlsx`, rows)
    } catch (error) {
      onError(getErrorMessage(error))
    } finally {
      setExportingExcel(false)
    }
  }

  const renderPagination = () => {
    if (pagesCount <= 1) return null
    return (
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <Text size="sm" color="muted">
          Página {page} de {pagesCount} ({filtered.length} {filtered.length === 1 ? 'registro' : 'registros'})
        </Text>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            Anterior
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pagesCount, p + 1))} disabled={page === pagesCount}>
            Próximo
          </Button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <>
      <Text size="sm" color="muted">
        Visão unificada de todos os documentos cadastrados no módulo de despesas.
      </Text>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="mb-3">
          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
            Filtros
          </Heading>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 [&>*]:min-w-0">
          <select
            value={filters.source}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, source: e.target.value as AllDocumentsFilters['source'] }))
              setPage(1)
            }}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm"
          >
            {SOURCE_FILTER_OPTIONS.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <Input
            type="text"
            placeholder="Emissor/Descrição"
            value={filters.issuer}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, issuer: e.target.value }))
              setPage(1)
            }}
          />
          <div
            className="flex min-w-0 items-center gap-2"
            title="Data inicial do documento (inclusiva)"
          >
            <span className="shrink-0 text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">
              De
            </span>
            <Input
              type="date"
              className="min-w-0 flex-1"
              aria-label="Data inicial"
              value={filters.dateFrom}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))
                setPage(1)
              }}
            />
          </div>
          <div
            className="flex min-w-0 items-center gap-2"
            title="Data final do documento (inclusiva)"
          >
            <span className="shrink-0 text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">
              Até
            </span>
            <Input
              type="date"
              className="min-w-0 flex-1"
              aria-label="Data final"
              value={filters.dateTo}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, dateTo: e.target.value }))
                setPage(1)
              }}
            />
          </div>
          <div className="col-span-full flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" type="button" onClick={clearFilters} disabled={!filtersActive}>
              Limpar filtros
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
              Todos os Documentos
            </Heading>
            <Text size="sm" color="muted">
              {filtered.length} {filtered.length === 1 ? 'registro' : 'registros'}
            </Text>
          </div>
          <div className="flex flex-row flex-nowrap items-center justify-end gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={exportFilteredExcel}
              disabled={filtered.length === 0 || exportingExcel}
              title={filtered.length === 0 ? 'Nenhum registro para exportar' : 'Baixar Excel com a relação filtrada'}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {exportingExcel ? 'Exportando...' : 'Baixar Excel'}
            </Button>
            <Button variant="outline" size="sm" type="button" onClick={() => setPrintDialogOpen(true)} disabled={filtered.length === 0}>
              <ArrowDownTrayIcon className="h-4 w-4" />
              Imprimir
            </Button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="p-8 text-center">
            <Text size="sm" color="muted">
              {filtersActive
                ? 'Nenhum documento encontrado para os filtros aplicados.'
                : 'Nenhum documento cadastrado.'}
            </Text>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Emissor/Descrição
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Criado em
                    </th>
                    {(canEdit || canDelete) && (
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Ações
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {paginated.map((doc) => (
                    <tr
                      key={`${doc.source}-${doc.id}`}
                      onClick={() => onViewDocument(doc)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${sourceBadgeClass(doc.source)}`}>
                          {doc.sourceLabel}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-xs truncate" title={doc.issuerName ?? undefined}>
                        {doc.issuerName?.trim() || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {formatDate(doc.date)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right whitespace-nowrap font-medium">
                        {formatCurrency(doc.totalValue)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {formatDate(doc.createdAt)}
                      </td>
                      {(canEdit || canDelete) && (
                        <td className="px-4 py-3 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <div className="inline-flex items-center gap-2">
                            {canEdit && (
                              <button
                                type="button"
                                title="Editar documento"
                                onClick={() => onEditDocument(doc)}
                                className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                type="button"
                                title="Excluir documento"
                                onClick={() => {
                                  setDeleteReason('')
                                  setDeleteModal(doc)
                                }}
                                className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderPagination()}
          </>
        )}
      </div>

      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <Heading as="h3" size="xl" className="text-gray-900 dark:text-white mb-2">
              Excluir documento
            </Heading>
            <Text size="sm" color="muted" className="mb-4">
              {documentLabel(deleteModal)}
            </Text>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Motivo da exclusão
            </label>
            <textarea
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
              rows={3}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Informe o motivo..."
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={closeDeleteModal} disabled={submittingDelete}>
                Cancelar
              </Button>
              <button
                type="button"
                onClick={() => { void confirmDelete() }}
                disabled={submittingDelete || !deleteReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition-colors"
              >
                {submittingDelete ? 'Excluindo...' : 'Confirmar Exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}

      {printDialogOpen && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
              Imprimir
            </Heading>
            <Text size="sm" color="muted" className="mb-4">
              Selecione o escopo da impressão da listagem unificada.
            </Text>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => handlePrint('page')}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-left"
              >
                Página atual
              </button>
              <button
                type="button"
                onClick={() => handlePrint('filtered')}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-left"
              >
                Todos os filtrados
              </button>
            </div>
            <div className="flex justify-end mt-4">
              <Button variant="outline" onClick={() => setPrintDialogOpen(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AdmDespesasAllDocumentsTab
